import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { DateTime, FormMessage, ListView, ListViewActions } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import DisconnectApp from './DisconnectApp';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: ${weights.light};
  line-height: 1.2;
`;

const Description = styled.p`
  max-width: 41.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${colors.grey55};
`;

const Connections = styled.div`
  margin-top: 2rem;
`;

const Name = styled.div`
  font-weight: ${weights.bold};
`;

const Verified = styled.div`
  margin-top: 0.125rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
`;

const Scopes = styled.div`
  font-size: 0.875rem;
`;

const Never = styled.span`
  color: ${colors.grey40};
`;

export default function ConnectedApps() {
  useDocumentTitle('Connected Apps');

  const api = useApi();
  const { workspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [errorMessage, setErrorMessage] = useState();
  const [disconnecting, setDisconnecting] = useState();

  const fetchConnections = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage();
    try {
      const { data } = await api.www.workspaces(workspace.id).oauthConnections().get();
      setConnections(data);
    } catch ({ message }) {
      // Without this the list renders empty, which reads as "you have authorized nothing" — the
      // opposite of the truth if the member has a connection they came here to revoke.
      setErrorMessage(message || 'Your connected applications could not be loaded. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [api, workspace]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Wrapper>
      <div>
        <Title>Connected Apps</Title>
        <Description>
          Applications you have authorized to use Ruddr as you, such as an AI assistant. Each one can only see and
          change what your permissions already allow. Disconnecting one revokes its access immediately.
        </Description>
      </div>

      {errorMessage && <FormMessage.Error spaceTop>{errorMessage}</FormMessage.Error>}

      <Connections>
        <ListView>
          <ListView.Header>
            <ListView.Column>Application</ListView.Column>
            <ListView.Column>Access</ListView.Column>
            <ListView.Column>Connected</ListView.Column>
            <ListView.Column>Last Used</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {connections.map((connection) => (
              <ListView.Row key={connection.clientId} data-testid="connection">
                <ListView.Cell>
                  <div>
                    <Name>{connection.name}</Name>
                    {/* A name is self-asserted; only a metadata-document client has a domain that was verified. */}
                    {connection.isClientIdMetadataDocument && <Verified>{hostOf(connection.clientId)}</Verified>}
                  </div>
                </ListView.Cell>
                <ListView.Cell>
                  <Scopes>{connection.scopes.map(({ description }) => description).join(' ')}</Scopes>
                </ListView.Cell>
                <ListView.Cell>
                  <DateTime value={connection.connectedAt} />
                </ListView.Cell>
                <ListView.Cell>
                  {connection.lastUsedAt ? <DateTime value={connection.lastUsedAt} /> : <Never>Never</Never>}
                </ListView.Cell>
                <ListViewActions>
                  <ListViewActions.Delete tooltip="Disconnect" onClick={() => setDisconnecting(connection)} />
                </ListViewActions>
              </ListView.Row>
            ))}

            {connections.length === 0 && <ListView.Empty />}
          </ListView.Body>
        </ListView>
      </Connections>

      {disconnecting !== undefined && (
        <DisconnectApp
          connection={disconnecting}
          onClose={() => setDisconnecting()}
          onDisconnect={() => fetchConnections()}
        />
      )}
    </Wrapper>
  );
}

function hostOf(value) {
  try {
    return new URL(value).host;
  } catch {
    return null;
  }
}
